import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {ARCHIVED_ANALYSIS_TOOLTIP_TEXT} from 'analysis-tool/AnalysisToolConstants';
import {AnalysisGraphQL, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {MAX_DATA_POINTS} from 'analysis-tool/charting/AnalysisChartingConstants';
import {getStepReferencesForViewActions} from 'analysis-tool/data/DataReferenceUtils';
import {checkCanModifyStep} from 'analysis-tool/EditStepHelpers';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import isArchived from 'archive/isArchived';
import {ChartNode, Chart} from 'charts/ChartTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import DataFrameTableWrapper, {
  DataFrameTableWrapperOverflowOption,
  RowSelectionProps,
} from 'data-frames/wrapper/DataFrameTableWrapper';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import getVisualizationType from 'insights/analytics-visualizations/data/getVisualizationType';
import PresentationalUniversalBlockView, {
  ErrorHandlingOptions,
} from 'insights/universal-blocks/PresentationalUniversalBlockView';
import {
  VisualizationConfig,
  VisualizationType,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {StepReference} from 'pipelines/PipelineDAO';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import {RegressionAnalysis} from 'ui-library/charting/statistics/regression/RegressionTypes';
import {SuccessfulAnalysis} from 'ui-library/charting/statistics/Types';
import OverflowMenu from 'ui-library/menus/OverflowMenu';
import removeNulls from 'util/removeNulls';

import {openFlagRowsModal} from '../flag-rows/FlagRowsModal';
import {getFlaggedRowsFromDataFrame} from '../flag-rows/FlagRowUtil';
import getDataFrameRenderOverrides from '../flag-rows/getDataFrameRenderOverrides';
import FilterPillListForVisualization from './FilterPillListForVisualization';
import getDatasetViewErrorHandlingOptions from './getDatasetViewErrorHandlingOptions';

function getOverflowOptions({
  analysis,
  onCopyClicked,
  onDeleteClicked,
  onPromoteClicked,
  viewSteps,
}: Pick<
  VisualizationBlockProps,
  'analysis' | 'onCopyClicked' | 'onDeleteClicked' | 'onPromoteClicked' | 'viewSteps'
>): Array<DataFrameTableWrapperOverflowOption> {
  if (isAnalysisReadOnly(analysis)) {
    return [];
  }

  const {canEdit, message} = checkCanModifyStep(analysis, viewSteps[0].id, 'view', true);
  const isAanalysisArchived = isArchived(analysis);
  const options: Array<DataFrameTableWrapperOverflowOption> = [];

  if (onCopyClicked != null) {
    options.push({
      icon: 'copy',
      onSelect: onCopyClicked,
      title: 'Copy view',
      isDisabled: isAanalysisArchived,
      tooltipTitle: isAanalysisArchived ? ARCHIVED_ANALYSIS_TOOLTIP_TEXT : undefined,
    });
  }

  if (onPromoteClicked != null) {
    options.push({
      icon: 'table',
      onSelect: onPromoteClicked,
      title: 'Promote view to table',
      isDisabled: isAanalysisArchived,
      tooltipTitle: isAanalysisArchived ? ARCHIVED_ANALYSIS_TOOLTIP_TEXT : undefined,
    });
  }

  if (onDeleteClicked != null) {
    options.push({
      icon: 'delete',
      onSelect: onDeleteClicked,
      title: 'Delete view',
      isDisabled: !canEdit,
      tooltipTitle: !canEdit ? message : undefined,
    });
  }

  return options;
}

function EditButton({
  analysis,
  onEditClicked,
  viewSteps,
}: Pick<VisualizationBlockProps, 'analysis' | 'onEditClicked' | 'viewSteps'>): JSX.Element {
  if (isAnalysisReadOnly(analysis)) {
    return <></>;
  }

  const {canEdit, message} = checkCanModifyStep(analysis, viewSteps[0].id, 'view', false);

  return (
    <Tooltip title={message ?? 'Edit view'}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        className="u-mini-spacing-left"
        disabled={!canEdit}
        icon="edit"
        onClick={onEditClicked}
        size={ButtonSize.MINI}
      />
    </Tooltip>
  );
}

function Toolbar({
  analysis,
  saveStatus,
  onCopyClicked,
  onDeleteClicked,
  onEditClicked,
  onPromoteClicked,
  viewSteps,
}: Pick<
  VisualizationBlockProps,
  | 'analysis'
  | 'saveStatus'
  | 'onCopyClicked'
  | 'onEditClicked'
  | 'onDeleteClicked'
  | 'onPromoteClicked'
  | 'viewSteps'
>): JSX.Element {
  const overflowOptions = getOverflowOptions({
    analysis,
    onCopyClicked,
    onDeleteClicked,
    onPromoteClicked,
    viewSteps,
  });
  return (
    <>
      <div className={classNames('u-mini-spacing-left', css(styles.saveStatus))}>{saveStatus}</div>
      {onEditClicked && (
        <EditButton analysis={analysis} onEditClicked={onEditClicked} viewSteps={viewSteps} />
      )}
      {overflowOptions.length > 0 && (
        <OverflowMenu
          menuProps={{dropLeft: true}}
          options={overflowOptions.map((option) => ({...option, contents: <span>{option.title}</span>}))}
        />
      )}
    </>
  );
}

function getRowSelectionProps({
  allowRowSelection,
  viewSteps,
  analysis,
  dataFrame,
}: {
  allowRowSelection?: boolean;
  analysis: AnalysisGraphQL;
  viewSteps: Array<GetAnalysisPipelineStep>;
  dataFrame: DataFrame;
}): RowSelectionProps | undefined {
  if (!allowRowSelection) {
    return undefined;
  }
  const maybeStepReferences = getStepReferencesForViewActions({
    viewSteps,
    pipelineStepType: PipelineStepType.FLAG_ROWS,
  });
  if (maybeStepReferences == null) {
    return undefined;
  }
  const {precedingStep, precedingStepData, outputDataReferences} = maybeStepReferences;
  const flaggedRows = getFlaggedRowsFromDataFrame(analysis, precedingStep, dataFrame.rows);
  return {
    flaggedRows,
    onSelectRows: (selectedRowIds: Set<string>, isAddingFlag: boolean) => {
      openFlagRowsModal({
        analysis,
        selectedRowIds,
        flaggedRowIds: flaggedRows,
        isAddingFlag,
        parentStepReference: {
          stepReference: {
            stepId: precedingStep.id,
            type: StepReference.STEP_ID,
          },
          stepDataName: precedingStepData.name,
        },
        outputDataReferences,
      });
    },
  };
}

export interface VisualizationBlockProps {
  allowRowSelection?: boolean;
  analysis: AnalysisGraphQL;
  banner: React.ReactNode;
  clientStateId: string;
  dataFrame: DataFrame;
  dataFrameId: string;
  chart?: ChartNode | Chart | null;
  chartError?: ErrorHandlingOptions | null;
  renderFromChart?: boolean;
  regressionAnalyses?: Array<SuccessfulAnalysis<RegressionAnalysis>>;
  saveStatus?: JSX.Element;
  title: React.ReactNode;
  viewSteps: Array<GetAnalysisPipelineStep>;
  visualizationConfig: VisualizationConfig;
  warnings?: Array<string>;
  saveToResultsOverflowOption?: DataFrameTableWrapperOverflowOption | null;
  /**
   * Callback to copy the visualization. If passed, shows option to copy the view.
   *
   * The option will be hidden if the analysis is read-only, disabled if the analyis is archived.
   */
  onCopyClicked?: () => void;
  /**
   * Callback to remove the visualization's view. If passed, shows option to delete the view.
   *
   * The option will be hidden if the analysis is read-only, and disabled if the analyis is archived.
   */
  onDeleteClicked?: () => void;
  /**
   * Callback to start editing the visualization's view. If passed, shows button to edit the view.
   *
   * The button will be hidden if the analysis is read-only, disabled if the analyis is archived.
   */
  onEditClicked?: () => void;
  /**
   * Callback to promote the visualization's data to a top-level table. If passed, shows option to promote
   * the view.
   *
   * The option will be hidden if the analysis is read-only, disabled if the analyis is archived.
   */
  onPromoteClicked?: () => void;
}

/**
 * A wrapper around PresentationalUniversalBlockView and DataFrameTableWrapper for visualization views.
 */
export default function VisualizationBlock({
  allowRowSelection,
  analysis,
  banner,
  clientStateId,
  dataFrame,
  dataFrameId,
  regressionAnalyses,
  saveStatus,
  title,
  viewSteps,
  visualizationConfig,
  renderFromChart = false,
  chart,
  chartError,
  warnings,
  saveToResultsOverflowOption,
  onCopyClicked,
  onDeleteClicked,
  onEditClicked,
  onPromoteClicked,
}: VisualizationBlockProps): JSX.Element {
  const visualizationType = visualizationConfig == null ? null : getVisualizationType(visualizationConfig);
  const blockBanner = (
    <>
      {banner}
      <FilterPillListForVisualization analysis={analysis} viewSteps={viewSteps} />
    </>
  );
  const saveStatusRenderFn = saveStatus == null ? null : () => saveStatus;
  const editButtonRenderFn =
    onEditClicked == null
      ? null
      : (): JSX.Element => (
          <EditButton
            key="edit-view-button"
            analysis={analysis}
            onEditClicked={onEditClicked}
            viewSteps={viewSteps}
          />
        );

  // If we have a valid chart preview and we are rendering from chart, then we can ignore the failed state of
  // the step being viewed, as it is not relevant to the current chart settings.
  const hasValidChart = renderFromChart && chart != null && chartError == null;
  // If the step type is CHART_VIEW and the visualizationType is TABLE, then we are converting a chart to a
  // and the error should be ignored as no longer relevant.
  const isChartViewConvertingToTable =
    visualizationType === VisualizationType.TABLE &&
    viewSteps[viewSteps.length - 1].definition.stepType === PipelineStepType.CHART_VIEW;
  const errorHandlingOptions =
    getDatasetViewErrorHandlingOptions(
      viewSteps,
      !analysis.permissions.writable,
      hasValidChart || isChartViewConvertingToTable
    ) ?? chartError;

  const tableOverflowOptions = getOverflowOptions({
    analysis,
    onCopyClicked,
    onDeleteClicked,
    onPromoteClicked,
    viewSteps,
  });
  if (saveToResultsOverflowOption != null) {
    tableOverflowOptions.push(saveToResultsOverflowOption);
  }

  return visualizationType === VisualizationType.TABLE && errorHandlingOptions == null ? (
    <DataFrameTableWrapper
      actionOrigin={ActionOrigin.ANALYSIS_TOOL}
      rowSelectionProps={getRowSelectionProps({allowRowSelection, viewSteps, analysis, dataFrame})}
      additionalElements={removeNulls([saveStatusRenderFn, editButtonRenderFn])}
      additionalOverflowOptions={tableOverflowOptions}
      banner={blockBanner}
      clientStateId={clientStateId}
      dataFrame={dataFrame}
      dataFrameId={dataFrameId}
      // TODO(BNCH-87910): Pass filter options to show filters in column headers
      // filterOptions={filterOptions}
      renderIsExcludedColumn={false}
      dataFrameRenderOverrides={getDataFrameRenderOverrides(analysis, viewSteps[viewSteps.length - 1])}
      title={title}
    />
  ) : (
    <PresentationalUniversalBlockView
      actionOrigin={ActionOrigin.ANALYSIS_TOOL}
      renderFromChart={renderFromChart}
      chart={chart}
      errorHandlingOptions={errorHandlingOptions}
      inputDataFrame={dataFrame}
      dataFrameRenderOverrides={getDataFrameRenderOverrides(analysis, viewSteps[viewSteps.length - 1])}
      viewStateId={clientStateId}
      visualizationConfig={visualizationConfig}
      regressionAnalyses={regressionAnalyses}
      maxRows={MAX_DATA_POINTS}
      paneProps={{
        banner: blockBanner,
        title,
        toolbar: (
          <Toolbar
            analysis={analysis}
            saveStatus={saveStatus}
            onEditClicked={onEditClicked}
            onDeleteClicked={onDeleteClicked}
            viewSteps={viewSteps}
            onCopyClicked={onCopyClicked}
            onPromoteClicked={onPromoteClicked}
          />
        ),
        warnings,
      }}
    />
  );
}

const styles = StyleSheet.create({
  saveStatus: {
    display: 'inline-flex',
  },
});
